import DateFilter from '@inovua/reactdatagrid-community/DateFilter';
import { IColumn } from '@inovua/reactdatagrid-community/types';
import { Text } from 'native-base';
import {
  TableOperatorNames,
  TableOperatorType,
} from 'ui/components/DataTable/@types/basicTypes';
import { PopOverOptions } from 'ui/components/PopOverOptions';
import { EActivityAttendanceTypes, LocationOptionsEnum } from 'ui/enums';
import { formattedDate } from 'ui/utils/formatter';

import Tag from 'ui/components/Tags/Tag';
import { TagIcon } from 'ui/components/Tags/Tag/types';
import { HelpButton } from 'ui/components/v2/Buttons/HelpButton';
import { ActivityTypeEnum } from '~/enums';
import { ApplicationSlots } from '~/pages/Authenticated/Activities/components/ApplicationSlots';
import { colors } from '~/theme/colors';
import { ActivityStatus, IActivity } from '~/types/interfaces/activity';
import { useActivityStatusModal } from './context/ActivityStatusModalContext';
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter';
import { useMemo } from 'react';

const statusConfig: Record<
  ActivityStatus,
  { color: string; icon: TagIcon; textColor: string }
> = {
  OPEN: {
    color: colors.success['50'],
    icon: 'check',
    textColor: colors.success['600'],
  },
  CLOSED: {
    color: colors.danger['50'],
    icon: 'x',
    textColor: colors.danger['600'],
  },
  ARCHIVED: {
    color: colors.gray['100'],
    icon: 'archive',
    textColor: colors.gray['600'],
  },
};

const ActivityActionsCell = ({ data }: { data: IActivity }) => {
  const { openModal } = useActivityStatusModal();

  const handleStatusChange = (status: ActivityStatus) => {
    openModal(data._id, status);
  };

  const options = useMemo(
    () => [
      {
        icon: {
          color: colors.gray[600],
          icon: 'repeat',
          size: 22,
        },
        title: 'Change status',
        submenu: [
          {
            icon: {
              color: colors.success[600],
              icon: 'check',
              size: 22,
            },
            title: 'OPEN',
            action: () => handleStatusChange(ActivityStatus.OPEN),
          },
          {
            icon: {
              color: colors.error[600],
              icon: 'x',
              size: 22,
            },
            title: 'CLOSED',
            action: () => handleStatusChange(ActivityStatus.CLOSED),
          },
          {
            icon: {
              color: colors.gray[600],
              icon: 'archive',
              size: 22,
            },
            title: 'ARCHIVED',
            action: () => handleStatusChange(ActivityStatus.ARCHIVED),
          },
        ],
      },
    ],
    [],
  );

  return (
    <PopOverOptions
      options={options}
      popovText="Options"
      popovTextPosition="right"
      popovIcon="more-vertical"
      popTextColor={colors.primary['600']}
      popovIconColor={colors.primary['600']}
    />
  );
};

export const getBaseColumns = (
  onOpenInfoModal?: () => void,
  activityType?: ActivityTypeEnum,
): IColumn[] => {
  const allColumns: IColumn[] = [
    {
      name: 'bookingsNumber',
      header: 'Applications',
      defaultFlex: 1,
      render: ({ data }: { data: IActivity }) => (
        <ApplicationSlots
          bookingsNumbers={data.bookingsNumber}
          volunteerNumber={data.volunteerNumber}
          isVolunteerNumberLimited={data.isVolunteerNumberLimited}
        />
      ),
    },
    {
      filterable: true,
      name: 'status',
      header: () => (
        <Text fontWeight={500} p={0} alignItems="center">
          Status
          <HelpButton size="13px" onPress={onOpenInfoModal} />
        </Text>
      ),
      headerProps: {
        style: {
          padding: 0,
          minHeight: 0,
        },
      },
      render: ({
        data: { status = ActivityStatus.OPEN },
      }: {
        data: IActivity;
      }) => (
        <Tag
          tagBgColor={statusConfig[status].color}
          tagTextColor={statusConfig[status].textColor}
          text={status}
          icon={statusConfig[status].icon}
          leftIconSize={16}
          showIcon
        />
      ),
      filterEditor: SelectFilter,
      filterEditorProps: {
        multiple: false,
        wrapMultiple: false,
        dataSource: Object.values(ActivityStatus).map((status) => {
          return {
            id: status,
            label: status,
          };
        }),
      },
    },
    {
      name: 'attendeesNumber',
      header: 'Participants',
      defaultFlex: 0.6,
      render: ({ data }: { data: IActivity }) => data.attendeesNumber,
    },
    {
      name: 'actions',
      header: 'Actions',
      defaultFlex: 0.5,
      render: ({ data }: { data: IActivity }) => (
        <ActivityActionsCell data={data} />
      ),
    },
  ];

  if (activityType && activityType !== ActivityTypeEnum.Event) {
    return allColumns.filter((col) => col.name !== 'attendeesNumber');
  }

  return allColumns;
};

export const columnsByActivityType = (
  activityType: ActivityTypeEnum,
  locationOption?: LocationOptionsEnum,
  onOpenInfoModal?: () => void,
): IColumn[] => {
  const addressColumn: IColumn = {
    name: 'address.street',
    header: 'Place',
    defaultFlex: 1.3,
    render: ({ data }: { data: IActivity }) => data.address.street,
  };
  const eventDateColumn: IColumn = {
    name: 'startDate',
    header: 'Date',
    defaultFlex: 0.4,
    filterEditor: DateFilter,
    filterEditorProps: () => ({
      dateFormat: 'YYYY-MM-DD',
      cancelButton: false,
      highlightWeekends: false,
    }),
    render: ({ data }: { data: IActivity }) =>
      data.startDate ? formattedDate(data.startDate, 'dd/MM/yyyy') : '',
  };

  const baseColumns = getBaseColumns(onOpenInfoModal, activityType);

  const actionColumns = [
    {
      name: 'place',
      header: 'Place',
      defaultFlex: 1,
      render: () => 'From home(No specific location)',
    },
    ...baseColumns,
  ];
  const eventColumns =
    locationOption !== LocationOptionsEnum.FromHome
      ? [addressColumn, eventDateColumn, ...baseColumns]
      : [eventDateColumn, ...baseColumns];

  const ongoingColumns =
    locationOption !== LocationOptionsEnum.FromHome
      ? [addressColumn, ...baseColumns]
      : [...baseColumns];

  return {
    [ActivityTypeEnum.Event]: eventColumns,
    [ActivityTypeEnum.Action]: actionColumns,
    // Opportunity doesn't need to close applications, so we don't show the action column.
    [ActivityTypeEnum.OngoingOpportunity]: ongoingColumns,
  }[activityType];
};

export const defaultFilterValues = [
  {
    name: 'address.street',
    operator: TableOperatorNames.Equals,
    type: TableOperatorType.String,
    value: '',
  },
  {
    name: 'meetingLink',
    operator: TableOperatorNames.Equals,
    type: TableOperatorType.String,
    value: '',
  },
  {
    name: 'startDate',
    operator: TableOperatorNames.AfterOrOn,
    type: TableOperatorType.Date,
    value: '',
  },
  {
    name: 'status',
    operator: TableOperatorNames.Equals,
    type: TableOperatorType.Select,
    value: '',
  },
  {
    name: 'bookingsNumber',
    operator: TableOperatorNames.GreaterThanOrEquals,
    type: TableOperatorType.String,
    value: '',
  },
  {
    name: 'attendeesNumber',
    operator: TableOperatorNames.GreaterThanOrEquals,
    type: TableOperatorType.String,
    value: '',
  },
];
